import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { GemMark } from '../common/BrandLogo';
import { APP_NAME, ROUTES } from '../../constants';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 236;

const NAV_SECTIONS: Array<{
  label: string;
  items: Array<{ label: string; to: string; icon: React.ReactNode; exact?: boolean }>;
}> = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', to: ROUTES.ADMIN, icon: <DashboardOutlinedIcon fontSize="small" />, exact: true }]
  },
  {
    label: 'Catalog',
    items: [
      { label: 'Products', to: `${ROUTES.ADMIN}/products`, icon: <DiamondOutlinedIcon fontSize="small" /> },
      { label: 'Categories', to: `${ROUTES.ADMIN}/categories`, icon: <CategoryOutlinedIcon fontSize="small" /> },
      { label: 'Reviews', to: `${ROUTES.ADMIN}/reviews`, icon: <RateReviewOutlinedIcon fontSize="small" /> }
    ]
  },
  {
    label: 'Sales',
    items: [
      { label: 'Orders', to: `${ROUTES.ADMIN}/orders`, icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
      { label: 'Coupons', to: `${ROUTES.ADMIN}/coupons`, icon: <LocalOfferOutlinedIcon fontSize="small" /> },
      { label: 'Customers', to: `${ROUTES.ADMIN}/customers`, icon: <PeopleAltOutlinedIcon fontSize="small" /> },
      {
        label: 'Abandoned carts',
        to: `${ROUTES.ADMIN}/abandoned-carts`,
        icon: <RemoveShoppingCartOutlinedIcon fontSize="small" />
      }
    ]
  },
  {
    label: 'Storefront',
    items: [
      { label: 'Homepage', to: `${ROUTES.ADMIN}/storefront`, icon: <StorefrontOutlinedIcon fontSize="small" /> },
      { label: 'Navigation', to: `${ROUTES.ADMIN}/navigation`, icon: <MenuOutlinedIcon fontSize="small" /> },
      { label: 'Footer', to: `${ROUTES.ADMIN}/footer`, icon: <ViewDayOutlinedIcon fontSize="small" /> }
    ]
  },
  {
    label: 'Configuration',
    items: [
      { label: 'Store settings', to: `${ROUTES.ADMIN}/settings`, icon: <SettingsOutlinedIcon fontSize="small" /> },
      {
        label: 'Pricing calculator',
        to: `${ROUTES.ADMIN}/pricing-calculator`,
        icon: <CalculateOutlinedIcon fontSize="small" />
      }
    ]
  }
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();

  const isSelected = (to: string, exact?: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 0,
            position: 'sticky',
            top: 0,
            height: '100vh',
            background: 'linear-gradient(180deg, #241A2E 0%, #1B1322 100%)',
            color: 'rgba(255,255,255,0.82)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 2.5 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.92)',
              borderRadius: 1.5,
              width: 34,
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GemMark size={22} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, lineHeight: 1.1, color: '#fff' }}>
              {APP_NAME}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em' }}>
              BACK OFFICE
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={{ pb: 1 }}>
          {NAV_SECTIONS.map((section) => (
            <List
              key={section.label}
              dense
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    backgroundColor: 'transparent',
                    color: 'rgba(255,255,255,0.38)',
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    lineHeight: '32px'
                  }}
                >
                  {section.label}
                </ListSubheader>
              }
            >
              {section.items.map((item) => {
                const selected = isSelected(item.to, item.exact);
                return (
                  <ListItemButton
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    selected={selected}
                    sx={{
                      mx: 1.25,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: selected ? '#fff' : 'rgba(255,255,255,0.72)',
                      '&.Mui-selected': {
                        background: 'linear-gradient(90deg, rgba(214,90,136,0.35), rgba(112,97,218,0.25))',
                        '&:hover': {
                          background: 'linear-gradient(90deg, rgba(214,90,136,0.45), rgba(112,97,218,0.32))'
                        }
                      },
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' }
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 600 : 400 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 'auto' }} />
        <Tooltip title="Open the storefront in a new tab">
          <ListItemButton
            component="a"
            href="/"
            target="_blank"
            sx={{ mx: 1.25, my: 1, borderRadius: 1.5, color: 'rgba(255,255,255,0.72)' }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 34 }}>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View store" primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        </Tooltip>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3.5 },
          minHeight: '100vh',
          backgroundColor: '#F7F5F8'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
